import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Anchor, Box, Button, Heading, Menu, ResponsiveContext, Tab, Tabs, Text, TextInput, Image, Stack } from "grommet";
import * as React from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import CustomButton from "../components/CustomButton";
import LinkAnchor from "../components/LinkAnchor";
import StyledModal from "../components/StyledModel";
import BulkCreateTeams from "./BulkCreateTeams";
import CreateTeam from "./CreateTeam";
import { TeamCard } from "./TeamCard";
import { TeamName } from "./TeamName";
import TeamProfile from "./TeamProfile";
import { getTeams, TeamViewModel } from "./TeamsStore";
import Avatar from "react-avatar";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
// @ts-ignore
const bulkPlus = <FontAwesomeIcon size="xl" icon={["fad", "layer-plus"]}></FontAwesomeIcon>;
// @ts-ignore
const teamPlus = <FontAwesomeIcon size="xl" icon={["fad", "users-medical"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;
const search = <FontAwesomeIcon icon={["fad", "search"]}></FontAwesomeIcon>;

export const TeamRow = ({ team, children, size = "medium" }: { team: TeamViewModel; children?: React.ReactElement; size?: string }) => {
    let BoxProps = {};

    const navigate = useNavigate();

    if (!children) {
        BoxProps = {
            hoverIndicator: "background-back",
            onClick: () => {
                navigate(`/teams/${team.id}`);
            },
        };
    }

    const responsiveSize = React.useContext(ResponsiveContext);

    return (
        <Box key={team.id} background={"background"} pad={size} gap="small" {...BoxProps} direction="row">
            <Box gap="small">
                {team.logoUri && <Image src={team.logoUri} alt="" style={{ maxHeight: "35px", objectFit: "contain" }} />}
                {!team.logoUri && <Avatar name={team.name} alt="" size="35px" />}
                <Box justify="center">
                    <LinkAnchor path={`/teams/${team.id}`} label={<TeamName team={team} size={size} color=""></TeamName>}></LinkAnchor>
                </Box>
            </Box>
            <Box justify="end" direction={responsiveSize == "small" ? "column" : "row"} width="100%">
                <Box justify="center">{children}</Box>
            </Box>
        </Box>
    );
};

export const TeamTile = ({ team }: { team: TeamViewModel }) => {
    return (
        <Link to={`/teams/${team.id}`} style={{ textDecoration: "none" }}>
            <Box key={team.id} background={"background-back"} pad="medium" gap="small" height="auto" justify="between" fill="vertical" hoverIndicator="background-front" onClick={() => {}}>
                <Box align="center" justify="center">
                    <Avatar name={team.name} src={team.logoUri} size="25px" alt="" />
                </Box>
                <Box>
                    <Box align="center" justify="center">
                        <TeamName team={team} size="medium" color=""></TeamName>
                    </Box>
                </Box>
            </Box>
        </Link>
    );
};

export const TeamMenu = ({ team }) => {
    const size = React.useContext(ResponsiveContext);
    const navigate = useNavigate();
    return (
        <>
            {size == "small" && (
                <Menu
                    items={[
                        {
                            label: "Profile",
                            onClick: () => {
                                navigate("/teams/" + team.id);
                            },
                        },
                        {
                            label: "Home Page",
                            onClick: () => {
                                //@ts-ignore
                                window.location = "/ue/teams/" + team.id;
                            },
                        },
                    ]}
                />
            )}
            {size != "small" && (
                <Box direction="row" gap="small">
                    <CustomButton primary path={"/teams/" + team.id} icon={edit} label="Profile"></CustomButton>
                    <CustomButton primary href={"/ue/teams/" + team.id} icon={idBadge} label="Home Page"></CustomButton>
                </Box>
            )}
        </>
    );
};

export const TeamGrid = ({ teams }) => {
    const size = React.useContext(ResponsiveContext);

    const BracketGridProps = size == "small" ? { rows: ["auto"], columns: ["1/2", "1/2"] } : { rows: ["auto"], columns: ["1/4", "1/4", "1/4", "1/4"] };

    return (
        <>
            <Box direction="row" gap="medium" wrap>
                {teams.map((team) => (
                    <Box margin={{ bottom: "medium" }} key={team.id}>
                        <Stack anchor="top-left">
                            <TeamCard team={team}></TeamCard>
                            {team.archived && (
                                <Box pad={"xsmall"} background={"#c27b00"} round="3px">
                                    <FontAwesomeIcon size="lg" icon={["fad", "archive"]} color="black" />
                                </Box>
                            )}
                        </Stack>
                    </Box>

                    // <TeamBlock team={team} key={team.id}></TeamBlock>
                ))}
            </Box>
        </>
    );
};

const TeamsManagement = () => {
    const user = useUser();

    const [teams, setTeams] = React.useState([] as TeamViewModel[]);
    const [filter, setFilter] = React.useState("");
    const [filteredTeams, setFilteredTeams] = React.useState([] as TeamViewModel[]);
    let { pathname } = useLocation();

    const [includeArchived, setIncludeArchived] = React.useState(false);

    React.useEffect(() => {
        getTeams(user.organization.id, includeArchived).then((teams) => {
            setTeams(teams);
        });
    }, [pathname, user, includeArchived]);

    React.useEffect(() => {
        if (filter) {
            setFilteredTeams(teams.filter((t) => t.name.toLowerCase().indexOf(filter.toLowerCase()) != -1));
        } else {
            setFilteredTeams(teams);
        }
    }, [teams, filter]);

    const [index, setIndex] = React.useState(0);
    const onActive = (nextIndex: number) => setIndex(nextIndex);

    const hasWritePermission = usePermission("sport:teams.write");

    const [showTeamsCreateModal, setShowTeamsCreateModal] = React.useState(false);
    const [showTeamsBulkCreateModal, setShowTeamsBulkCreateModal] = React.useState(false);

    return (
        <div>
            <Routes>
                <Route path={"create"} element={<>{hasWritePermission && <CreateTeam />}</>}></Route>
                <Route path={"createBulk"} element={<>{hasWritePermission && <BulkCreateTeams />}</>}></Route>
                <Route path={":id/*"} element={<TeamProfile />}></Route>
                <Route
                    path={""}
                    element={
                        <>
                            {/* <Heading size="small" textAlign="center" fill>
                                Teams
                            </Heading> */}
                            <Box direction="row" gap="small" justify="between">
                                {hasWritePermission && (
                                    <Box direction="row" gap="small">
                                        {/* <CustomButton path={"/teams/create"} icon={teamPlus} label="Add Team"></CustomButton>
                                        <CustomButton path={"/teams/createBulk"} icon={bulkPlus} label="Bulk Add"></CustomButton> */}
                                        <CustomButton onClick={() => setShowTeamsCreateModal(true)} icon={teamPlus} label="Add Team"></CustomButton>
                                        <CustomButton onClick={() => setShowTeamsBulkCreateModal(true)} icon={bulkPlus} label="Bulk Add"></CustomButton>
                                    </Box>
                                )}
                                <Box direction="row" justify="end" gap="small" align="center">
                                    <Box pad="small">
                                        <TextInput icon={search} placeholder="Search..." value={filter} onChange={(e) => setFilter(e.target.value)}></TextInput>
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => setIncludeArchived(!includeArchived)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "archive"]} color={includeArchived ? "#c27b00" : ""} />
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(0)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "grid-2"]} color={index == 0 ? "var(--accent)" : ""} />
                                    </Box>
                                    <Box pad="small" hoverIndicator="background-back" onClick={() => onActive(1)} round="5px">
                                        <FontAwesomeIcon size="lg" icon={["fad", "list"]} color={index == 1 ? "var(--accent)" : ""} />
                                    </Box>
                                </Box>
                            </Box>
                            <Tabs activeIndex={index} onActive={onActive}>
                                <Tab>
                                    <TeamGrid teams={filteredTeams}></TeamGrid>
                                </Tab>
                                <Tab>
                                    <Box gap="small">
                                        {filteredTeams.map((team) => (
                                            <TeamRow team={team} key={team.id}>
                                                <Box direction="row" gap="small" align="center">
                                                    {team.archived && (
                                                        <Box background="status-warning" pad={"xxsmall"} round="3px">
                                                            ARCHIVED
                                                        </Box>
                                                    )}
                                                    <TeamMenu team={team}></TeamMenu>
                                                </Box>
                                            </TeamRow>
                                        ))}
                                    </Box>
                                </Tab>
                            </Tabs>
                            <Box pad={{ top: "small" }}>
                                {teams.length != filteredTeams.length && (
                                    <Text>
                                        Showing {filteredTeams.length} of {teams.length}{" "}
                                        <Anchor href="#" onClick={() => setFilter("")}>
                                            Show All
                                        </Anchor>
                                    </Text>
                                )}
                            </Box>

                            <StyledModal isOpen={showTeamsCreateModal} setIsOpen={() => setShowTeamsCreateModal(false)}>
                                <CreateTeam />
                            </StyledModal>
                            <StyledModal isOpen={showTeamsBulkCreateModal} setIsOpen={() => setShowTeamsBulkCreateModal(false)}>
                                <BulkCreateTeams />
                            </StyledModal>
                        </>
                    }
                ></Route>
            </Routes>
        </div>
    );
};

export default TeamsManagement;
